<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SettingsHeader from './SettingsHeader.vue';
const props = defineProps({
  headerTitle: { type: String, default: '' },
  icon: { type: String, default: '' },
  keepAlive: { type: Boolean, default: true },
  showBackButton: { type: Boolean, default: false },
  backUrl: { type: [String, Object], default: '' },
  fullWidth: { type: Boolean, default: false },
  largeWidth: { type: Boolean, default: false },
  fixedHeight: { type: Boolean, default: false },
});

const route = useRoute();
const { t } = useI18n();

const showSettingsHeader = computed(
  () => props.headerTitle || props.icon || props.showBackButton
);

const maxWidthClass = computed(() => {
  if (props.fullWidth) return '';
  if (props.largeWidth) return 'max-w-[104rem]';
  return 'max-w-6xl';
});

const translatedHeaderTitle = computed(() => {
  const title = props.headerTitle;
  return title ? t(title) : '';
});

const translatedButtonText = computed(() => {
  const buttonText = props.headerButtonText;
  return buttonText ? t(buttonText) : '';
});

// Exclude hash so section anchors don't remount. Keep booking-widget show/new
// on a stable key so locale switches soft-load instead of remounting.
const viewKey = computed(() => {
  if (
    route.name === 'settings_booking_widgets_show' ||
    route.name === 'settings_booking_widgets_new'
  ) {
    return String(route.name);
  }
  const query = route.fullPath.includes('?')
    ? route.fullPath.slice(route.path.length).split('#')[0]
    : '';
  return `${route.path}${query}`;
});
</script>

<template>
  <div
    class="flex flex-1 flex-col m-0 bg-n-surface-1"
    :class="
      fixedHeight
        ? 'h-full min-h-0 overflow-hidden'
        : 'overflow-auto'
    "
  >
    <div
      class="mx-auto w-full flex flex-col flex-1 min-h-0"
      :class="[maxWidthClass, { 'overflow-hidden': fixedHeight }]"
    >
      <SettingsHeader
        v-if="showSettingsHeader"
        :icon="icon"
        :header-title="translatedHeaderTitle"
        :button-text="translatedButtonText"
        :show-back-button="showBackButton"
        :back-url="backUrl"
        class="sticky top-0 z-20"
        :class="{
          [maxWidthClass + ' w-full mx-auto']: fullWidth || largeWidth,
        }"
      />

      <div
        class="flex min-h-0 flex-1 flex-col overflow-hidden"
        :class="{ 'px-5': !fullWidth && !largeWidth }"
      >
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            v-if="!keepAlive"
            :key="viewKey"
            class="h-full min-h-0"
          />
          <keep-alive v-else>
            <component
              :is="Component"
              :key="viewKey"
              class="h-full min-h-0"
            />
          </keep-alive>
        </router-view>
      </div>
    </div>
  </div>
</template>
